<script lang="ts">
  import { tick } from 'svelte';

  import Modal from '@shared/ui/Modal.svelte';
  import SalesActionIssueTagField from './SalesActionIssueTagField.svelte';
  import {
    buildInitialSalesActionDraft,
    isSalesActionDraftDirty,
    takeSalesActionDraftSnapshot,
    type SalesActionDraftMode,
    type SalesActionDraftSnapshot,
    type SalesActionDraftValues,
  } from './sales-action-draft';
  import {
    createSalesAction,
    patchSalesAction,
  } from '@entities/sales-action/store.svelte';
  import { LOST_REASON_OPTIONS } from '@shared/constants/choices';
  import {
    NEXT_ACTION_BY_STATUS,
    isNextActionAllowedForStatus,
  } from '@shared/constants/next-action-matrix';
  import {
    ACTION_TYPE_LABELS,
    LOST_REASON_LABELS,
    SALES_STATUS_LABELS,
    SALES_STATUS_ORDER,
    type ActionType,
    type LostReason,
    type SalesAction,
    type SalesActionCreateRequest,
    type SalesActionPatchRequest,
    type SalesActionTarget,
    type SalesStatus,
  } from '@shared/types/sales-action';
  import type { Lawyer } from '@domain/lawyer/model';
  import { salesStatusRequiresProduct } from '@shared/constants/sales-action-display';

  interface Props {
    open: boolean;
    target: SalesActionTarget;
    /// Existing row to edit. When undefined the modal creates a new sales
    /// action (POST), otherwise it issues a PATCH.
    existing?: SalesAction;
    /// Solutions selectable in the create form (id + display name). On edit
    /// the solution is fixed.
    solutionOptions?: Array<{ id: string; label: string }>;
    /// その事務所の所属弁護士。対象弁護士 (catalog No.116) のマルチセレクト
    /// 候補。 AddDialogueLogModal の「顧客側参加者」 と同じ仕組み。
    lawyers?: Lawyer[];
    onClose: () => void;
    onSaved: (action: SalesAction) => void;
  }

  let {
    open,
    target,
    existing,
    solutionOptions = [],
    lawyers = [],
    onClose,
    onSaved,
  }: Props = $props();
  const lostReasonOptions = LOST_REASON_OPTIONS as Array<{
    value: LostReason;
    label: string;
  }>;

  const isEdit = $derived(existing !== undefined);
  // 対象弁護士の必須対象 (86eya0dva 書き込み整合): 行自身のアンカー弁護士
  // (行の target が弁護士の場合のその弁護士)。 編集時は既存行自身の target を見る
  // (閲覧中の画面の弁護士ではない — firm アンカー行を弁護士詳細から開いても
  // ロックしない)。 作成時は、この target がそのまま新規行の target になる。
  const anchorLawyerId = $derived.by((): string | undefined => {
    const rowTarget = existing ? existing.target : target;
    return rowTarget.kind === 'lawyer' ? rowTarget.id : undefined;
  });

  let solutionId = $state(existing?.product_id ?? '');
  // 商談前の候補ソリューション (複数・任意)。 商談以降では solutionId (単一) を使う。
  let candidateSolutionIds = $state<string[]>(
    existing?.candidate_product_ids ?? [],
  );
  let salesStatus = $state<SalesStatus>(existing?.sales_status ?? 'no_contact');
  let actionType = $state<ActionType | ''>(existing?.action_type ?? '');
  let actionTitle = $state(existing?.action_title ?? '');
  let dueDate = $state(existing?.due_date ?? '');
  let note = $state(existing?.note ?? '');
  let lostReason = $state<LostReason | ''>(existing?.lost_reason ?? '');
  let lostReasonNote = $state(existing?.lost_reason_note ?? '');
  let sourceCustomerProblemId = $state(
    existing?.source_customer_problem_id ?? '',
  );
  // 対象弁護士 (catalog No.116)。
  let participantLawyerIds = $state<string[]>(
    existing?.participant_lawyer_ids ?? [],
  );

  let submitting = $state(false);
  let serverError = $state<string | null>(null);
  let openingSnapshot = $state<SalesActionDraftSnapshot | null>(null);
  let discardConfirmOpen = $state(false);
  let continueButton = $state<HTMLButtonElement>();
  let formElement = $state<HTMLFormElement>();
  let lastFocusedElement: HTMLElement | null = null;

  const draftMode = $derived<SalesActionDraftMode>(isEdit ? 'edit' : 'create');
  const currentDraft = $derived.by(
    (): SalesActionDraftValues => ({
      solutionId,
      candidateSolutionIds,
      salesStatus,
      actionType,
      actionTitle,
      dueDate,
      note,
      lostReason,
      lostReasonNote,
      sourceCustomerProblemId,
      participantLawyerIds,
    }),
  );
  const isDirty = $derived(
    openingSnapshot !== null &&
      isSalesActionDraftDirty(openingSnapshot, draftMode, currentDraft),
  );

  $effect(() => {
    // Re-seed defaults when the modal opens with a different `existing`.
    if (!open) return;
    const seed = buildInitialSalesActionDraft(existing, anchorLawyerId);
    const mode: SalesActionDraftMode = existing ? 'edit' : 'create';
    solutionId = seed.solutionId;
    candidateSolutionIds = seed.candidateSolutionIds;
    salesStatus = seed.salesStatus;
    actionType = seed.actionType;
    actionTitle = seed.actionTitle;
    dueDate = seed.dueDate;
    note = seed.note;
    lostReason = seed.lostReason;
    lostReasonNote = seed.lostReasonNote;
    sourceCustomerProblemId = seed.sourceCustomerProblemId;
    participantLawyerIds = seed.participantLawyerIds;
    openingSnapshot = takeSalesActionDraftSnapshot(mode, seed);
    discardConfirmOpen = false;
    lastFocusedElement = null;
    serverError = null;
  });

  function requestClose() {
    if (submitting) return;
    if (discardConfirmOpen) {
      void continueEditing();
      return;
    }
    if (isDirty) {
      void showDiscardConfirmation();
      return;
    }
    onClose();
  }

  async function showDiscardConfirmation() {
    const activeElement = document.activeElement;
    lastFocusedElement =
      activeElement instanceof HTMLElement ? activeElement : null;
    discardConfirmOpen = true;
    await tick();
    continueButton?.focus();
  }

  async function continueEditing() {
    discardConfirmOpen = false;
    await tick();
    if (lastFocusedElement?.isConnected) {
      lastFocusedElement.focus();
      return;
    }
    formElement
      ?.querySelector<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      )
      ?.focus();
  }

  function discardAndClose() {
    discardConfirmOpen = false;
    lastFocusedElement = null;
    onClose();
  }

  function toggleParticipant(id: string) {
    // アンカー弁護士は解除不可の必須対象 (86eya0dva)。 API が黙って再追加すると
    // 画面表示と保存結果がずれるため、 UI 側で外せないようにして整合させる。
    if (id === anchorLawyerId) return;
    participantLawyerIds = participantLawyerIds.includes(id)
      ? participantLawyerIds.filter((x) => x !== id)
      : [...participantLawyerIds, id];
  }

  function toggleCandidateSolution(id: string) {
    candidateSolutionIds = candidateSolutionIds.includes(id)
      ? candidateSolutionIds.filter((x) => x !== id)
      : [...candidateSolutionIds, id];
  }

  function solutionLabel(id: string | null | undefined): string {
    if (!id) return '未設定';
    return solutionOptions.find((s) => s.id === id)?.label ?? id;
  }

  function lostReasonDisplay(): string {
    if (!existing || existing.sales_status !== 'lost') return '—';
    if (!existing.lost_reason) return '—';
    const label =
      LOST_REASON_LABELS[existing.lost_reason] ?? existing.lost_reason;
    const note = existing.lost_reason_note?.trim();
    return note ? `${label}（${note}）` : label;
  }

  const modalTitle = $derived(
    !isEdit || existing?.sales_status === 'no_contact'
      ? '営業アクションを追加'
      : '営業アクションを編集',
  );
  const activeModalTitle = $derived(
    discardConfirmOpen ? '入力内容を破棄しますか？' : modalTitle,
  );
  const missingRequiredSolution = $derived(
    !isEdit &&
      salesStatusRequiresProduct(salesStatus) &&
      solutionId.trim() === '',
  );
  const isCreatingLost = $derived(!isEdit && salesStatus === 'lost');
  const actionTypeCandidates = $derived(NEXT_ACTION_BY_STATUS[salesStatus]);
  const hasActionTypeCandidates = $derived(actionTypeCandidates.length > 0);

  $effect(() => {
    if (!open || actionType === '') return;
    if (!isNextActionAllowedForStatus(salesStatus, actionType)) {
      actionType = '';
    }
  });

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    submitting = true;
    serverError = null;
    if (missingRequiredSolution) {
      serverError = '商談以降はソリューションが必須です';
      submitting = false;
      return;
    }
    if (isCreatingLost && (lostReason === '' || lostReasonNote.trim() === '')) {
      serverError = '失注理由と自由記述を入力してください';
      submitting = false;
      return;
    }
    try {
      let saved: SalesAction;
      if (existing) {
        const patch: SalesActionPatchRequest = {
          participant_lawyer_ids: participantLawyerIds,
          action_type: actionType === '' ? null : actionType,
          action_title: actionTitle.trim() === '' ? null : actionTitle.trim(),
          due_date: dueDate === '' ? null : dueDate,
          note: note.trim() === '' ? null : note.trim(),
          source_customer_problem_id:
            sourceCustomerProblemId.trim() === ''
              ? null
              : sourceCustomerProblemId,
        };
        saved = await patchSalesAction(existing.id, patch);
      } else {
        const dealStatus = salesStatusRequiresProduct(salesStatus);
        const body: SalesActionCreateRequest = {
          target,
          participant_lawyer_ids: participantLawyerIds,
          // 商談以降は案件ソリューション (単一)、商談前は候補 (複数) として保存。
          // 意味を混ぜないよう、それぞれ一方のみ値を持たせる。
          product_id: dealStatus
            ? solutionId.trim() === ''
              ? null
              : solutionId
            : null,
          candidate_product_ids: dealStatus ? [] : candidateSolutionIds,
          sales_status: salesStatus,
          action_type: actionType === '' ? null : actionType,
          action_title: actionTitle.trim() === '' ? null : actionTitle.trim(),
          due_date: dueDate === '' ? null : dueDate,
          note: note.trim() === '' ? null : note.trim(),
          lost_reason: isCreatingLost && lostReason !== '' ? lostReason : null,
          lost_reason_note: isCreatingLost ? lostReasonNote.trim() : null,
          source_customer_problem_id:
            sourceCustomerProblemId.trim() === ''
              ? null
              : sourceCustomerProblemId,
        };
        saved = await createSalesAction(body);
      }
      onSaved(saved);
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal
  {open}
  title={activeModalTitle}
  onClose={requestClose}
  closeOnBackdrop={false}
  closeDisabled={submitting}
  descriptionId={discardConfirmOpen
    ? 'sales-action-discard-description'
    : undefined}
>
  <form
    id="sales-action-edit-form"
    bind:this={formElement}
    class:hidden={discardConfirmOpen}
    aria-hidden={discardConfirmOpen}
    onsubmit={handleSubmit}
  >
    {#if !isEdit}
      <label>
        <span>ステータス</span>
        <select bind:value={salesStatus}>
          {#each SALES_STATUS_ORDER as s (s)}
            <option value={s}>{SALES_STATUS_LABELS[s]}</option>
          {/each}
        </select>
      </label>
    {:else}
      <div class="readonly-field">
        <span>ステータス</span>
        <div class="readonly-value">{SALES_STATUS_LABELS[salesStatus]}</div>
      </div>
    {/if}

    {#if hasActionTypeCandidates}
      <label>
        <span>次アクション</span>
        <select bind:value={actionType} disabled={submitting}>
          <option value="">未設定</option>
          {#each actionTypeCandidates as t (t)}
            <option value={t}>{ACTION_TYPE_LABELS[t]}</option>
          {/each}
        </select>
      </label>
    {/if}

    <label>
      <span>対応期限</span>
      <input type="date" bind:value={dueDate} />
    </label>

    <fieldset>
      <legend>対象弁護士</legend>
      {#if lawyers.length > 0}
        <div class="checks">
          {#each lawyers as l (l.id)}
            {@const isAnchor = l.id === anchorLawyerId}
            <label class="check">
              <input
                type="checkbox"
                checked={participantLawyerIds.includes(l.id)}
                disabled={isAnchor}
                onchange={() => toggleParticipant(l.id)}
              />
              <span>{l.full_name}</span>
              {#if isAnchor}
                <em class="anchor-badge">必須</em>
              {/if}
            </label>
          {/each}
        </div>
      {:else}
        <small class="muted">所属弁護士が登録されていません。</small>
      {/if}
    </fieldset>

    <label>
      <span>次アクション詳細</span>
      <input bind:value={actionTitle} maxlength="200" />
    </label>

    <SalesActionIssueTagField
      {target}
      value={sourceCustomerProblemId}
      currentLabel={existing?.source_problem?.problem_name ?? null}
      disabled={submitting}
      showDerived={false}
      onChange={(value) => (sourceCustomerProblemId = value)}
    />

    {#if !isEdit}
      {#if salesStatusRequiresProduct(salesStatus)}
        <!-- 商談以降: 案件ソリューション (単一・必須)。 -->
        <label>
          <span>ソリューション <em>*</em></span>
          <select
            bind:value={solutionId}
            disabled={solutionOptions.length === 0}
          >
            {#if solutionOptions.length === 0}
              <option value="">(ソリューション読込中…)</option>
            {:else}
              <option value="">未設定</option>
              {#each solutionOptions as opt (opt.id)}
                <option value={opt.id}>{opt.label}</option>
              {/each}
            {/if}
          </select>
        </label>
        {#if missingRequiredSolution}
          <small class="error">商談以降はソリューションが必須です。</small>
        {/if}
      {:else}
        <!-- 商談前: 候補ソリューション (複数・任意)。 -->
        <fieldset>
          <legend>ソリューション（候補 / 任意・複数）</legend>
          {#if solutionOptions.length === 0}
            <small class="muted">ソリューションがありません。</small>
          {:else}
            <div class="checks">
              {#each solutionOptions as opt (opt.id)}
                <label class="check">
                  <input
                    type="checkbox"
                    checked={candidateSolutionIds.includes(opt.id)}
                    onchange={() => toggleCandidateSolution(opt.id)}
                  />
                  <span>{opt.label}</span>
                </label>
              {/each}
            </div>
          {/if}
        </fieldset>
      {/if}
    {:else}
      <div class="readonly-field">
        <span>ソリューション</span>
        <div class="readonly-value">{solutionLabel(existing?.product_id)}</div>
      </div>
    {/if}

    <label>
      <span>メモ</span>
      <textarea bind:value={note} maxlength="1000" rows="3"></textarea>
    </label>

    {#if isCreatingLost}
      <fieldset class="lost-reason">
        <legend>失注理由</legend>
        <label>
          <span>理由 <em>*</em></span>
          <select bind:value={lostReason} required>
            <option value="">選択してください</option>
            {#each lostReasonOptions as option (option.value)}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
        </label>
        <label>
          <span>自由記述 <em>*</em></span>
          <textarea bind:value={lostReasonNote} rows="3" required></textarea>
        </label>
      </fieldset>
    {:else if existing?.sales_status === 'lost'}
      <div class="readonly-field">
        <span>失注理由</span>
        <div class="readonly-value">{lostReasonDisplay()}</div>
      </div>
    {/if}

    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>

  {#if discardConfirmOpen}
    <div class="discard-confirmation">
      <p id="sales-action-discard-description">
        保存されていない入力内容があります。閉じると入力内容は失われます。
      </p>
    </div>
  {/if}

  {#snippet footer()}
    {#if discardConfirmOpen}
      <button bind:this={continueButton} type="button" onclick={continueEditing}
        >入力を続ける</button
      >
      <button type="button" class="danger" onclick={discardAndClose}
        >破棄して閉じる</button
      >
    {:else}
      <button type="button" onclick={requestClose} disabled={submitting}
        >キャンセル</button
      >
      <button
        type="submit"
        form="sales-action-edit-form"
        class="primary"
        disabled={submitting}
      >
        {submitting ? '保存中…' : '保存'}
      </button>
    {/if}
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  form.hidden {
    display: none;
  }
  .discard-confirmation {
    min-height: 96px;
    display: flex;
    align-items: center;
  }
  .discard-confirmation p {
    margin: 0;
    color: #374151;
    line-height: 1.6;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  input,
  select,
  textarea {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  textarea {
    resize: vertical;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 8px 10px;
    margin: 0;
  }
  fieldset.lost-reason {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  legend {
    font-size: 0.8rem;
    color: #374151;
    padding: 0 4px;
  }
  .readonly-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .readonly-value {
    min-height: 20px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #f9fafb;
    color: #111827;
    overflow-wrap: anywhere;
  }
  .checks {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }
  .check {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.8125rem;
  }
  .anchor-badge {
    color: #b91c1c;
    font-style: normal;
    font-size: 0.6875rem;
  }
  .check input {
    padding: 0;
  }
  .muted {
    color: #6b7280;
    font-size: 0.8125rem;
  }
  .error {
    color: #b91c1c;
    font-size: 0.8125rem;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button.danger {
    background: #b91c1c;
    border-color: #b91c1c;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
